/* eslint-disable */
import { Dialog } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { mapMimeTypeToAttachmentType } from 'libs/shared/domain/src/lib/application/mime-type-to-attachment';
import { GuidelineService } from 'libs/shared/domain/src/lib/infrastructure/guideline-page-domain/guideline.service';
import { LinksService } from 'libs/shared/domain/src/lib/infrastructure/links.service';
import { ScrollService } from 'libs/shared/domain/src/lib/infrastructure/scroll.service';
import { Attachment } from 'libs/shared/model/src/lib/attachments.model';
import { NewGuidelineChapter } from 'libs/shared/model/src/lib/guideline-chapter.model';
import { GuidelineActionDto } from 'libs/shared/model/src/lib/GuidelineActionDto';
import { GuidelineActionSignificance } from 'libs/shared/model/src/lib/GuidelineActionSignificance';
import { GuidelineClassificationItemDto } from 'libs/shared/model/src/lib/GuidelineClassificationItemDto';
import { GuidelineConditionDto } from 'libs/shared/model/src/lib/GuidelineConditionDto';
import { GuidelineCriterionDto } from 'libs/shared/model/src/lib/GuidelineCriterionDto';
import { GuidelineLinkDto } from 'libs/shared/model/src/lib/GuidelineLinkDto';
import { GuidelineObservationDto } from 'libs/shared/model/src/lib/GuidelineObservationDto';
import { Reference } from 'libs/shared/model/src/lib/reference.model';
import { NodeDetailPopoverData, NodeDetailPopoverTab } from 'libs/shared/model/src/lib/types/node-detail-popover-data.type';
import { ReferenceWithNumber } from 'libs/shared/model/src/lib/types/reference-with-number.type';
import { GuidelineElementType } from 'libs/shared/reference-numbering/src/lib/guideline-reference.model';
import { ReferenceNumberingService } from 'libs/shared/reference-numbering/src/lib/reference-numbering.service';
import { ChipComponent } from 'libs/shared/ui-common/src/lib/components/chip/chip.component';
import { IconDirective } from 'libs/shared/ui-common/src/lib/components/icon/icon.directive';
import { PopoverService } from 'libs/shared/ui-common/src/lib/components/popover/popover.service';
import { TooltipDirective } from 'libs/shared/ui-common/src/lib/components/tooltip/tooltip.directive';
import { NodeDetailPopoverComponent } from 'shared/feature-node-detail-popover';

export type GuidelineComponent =
  | NewGuidelineChapter
  | GuidelineActionDto
  | GuidelineClassificationItemDto
  | GuidelineConditionDto
  | GuidelineCriterionDto
  | GuidelineObservationDto;

@Component({
  selector: 'tt-attachments-links-references-tag',
  templateUrl: './attachments-links-references-tag.component.html',
  styleUrls: ['./attachments-links-references-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatIconModule, IconDirective, TranslocoPipe, TooltipDirective, ChipComponent],
})
export class AttachmentsLinksReferencesTag {
  private guidelineService = inject(GuidelineService);
  private dialog = inject(Dialog);
  private overlay = inject(Overlay);
  private popoverService = inject(PopoverService);
  private scrollService = inject(ScrollService);
  private linksService = inject(LinksService);

  @Input() public showPopover = true;

  public references = input<Reference[]>([]);
  public attachments = input<Attachment[]>([]);
  public links = input<GuidelineLinkDto[]>([]);
  public optional = input(false);
  public medicationRequests = input<GuidelineActionDto[]>([]);

  public isPublicPage = input(false);
  public isPreviewMode = input(false);
  public isUpdateReviewMode = input(false);
  public isEditImportedMode = input(false);

  private mappedAttachments = computed(() => {
    return this.attachments().map((attachment) => ({
      id: attachment.id ? attachment.id : '',
      title: this.getAttachmentTitle(attachment),
      fileType: mapMimeTypeToAttachmentType(attachment.contentType ? attachment.contentType : ''),
      updatedAtTimestamp: attachment.updatedAt || attachment.createdAt,
    }));
  });

  protected referenceWithNumbers = computed<ReferenceWithNumber[]>(() => {
    const references = this.guidelineService.orderedReferences().filter((reference) =>
      this.references()
        .map((ref) => ref?.id)
        .includes(reference.id),
    );

    return references ?? [];
  });

  protected referencesText = computed(() =>
    this.referenceWithNumbers()
      .map((reference) => `[${reference.referenceNumber}]`)
      .join(', '),
  );

  protected openPopover(event: MouseEvent, selectedTab?: NodeDetailPopoverTab) {
    event.stopPropagation();

    if (!this.showPopover) {
      return;
    }

    const providers = [
      { provide: ReferenceNumberingService, useValue: this.guidelineService },
      { provide: ScrollService, useValue: this.scrollService },
      { provide: LinksService, useValue: this.linksService },
    ];

    const data: NodeDetailPopoverData = {
      references: this.referenceWithNumbers(),
      links: this.links(),
      attachments: this.mappedAttachments(),
      selectedTab,
      medications: this.medicationRequests(),
      isEditImportedMode: this.isEditImportedMode(),
      isPreviewMode: this.isPreviewMode(),
      isUpdateReviewMode: this.isUpdateReviewMode(),
    };

    if (window.innerWidth < 480) {
      this.dialog.open(NodeDetailPopoverComponent, {
        width: '100vw',
        maxWidth: '100vw',
        data,
        autoFocus: 'none',
        positionStrategy: this.overlay.position().global().bottom(),
        providers,
      });
    } else {
      this.popoverService.open(NodeDetailPopoverComponent, event.currentTarget as HTMLElement, {
        data,
        positionStrategy: 'top-right',
        offsetY: 8,
        hasBackdrop: false,
        providers,
      });
    }
  }

  private getAttachmentTitle(attachment: Attachment): string {
    if (attachment.displayName && attachment.displayName !== 'null') {
      return attachment.displayName;
    }

    return attachment.originalName ?? '';
  }

  protected readonly GuidelineElementType = GuidelineElementType;
  protected readonly open = open;
  protected readonly NodeDetailPopoverTab = NodeDetailPopoverTab;
  protected readonly GuidelineActionSignificance = GuidelineActionSignificance;
}
